/* eslint-disable camelcase */
import cloneDeep from 'lodash/cloneDeep'
import { timeRecordingFilterTabs, timeRecordingStatuses } from '@/helpers/constants'
import { addSearchEvent, addFilterEvent } from '@/helpers/gtm'

const timeRecordingStatusKeys = {}
Object.keys(timeRecordingStatuses).forEach(key => (timeRecordingStatusKeys[timeRecordingStatuses[key]] = key))

const state = () => ({
  filter: {
    keyword: [],
    timeframe_start_date: null,
    timeframe_end_date: null,
    submitted_start_date: null,
    submitted_end_date: null,
    freelancers: [],
    projects: [],
    agencies: [],
    status: []
  },
  selectedTab: timeRecordingFilterTabs.all
})

export default {
  namespaced: true,
  state,
  getters: {
    hasAnyFilterApplied ({ filter }, getters, rootState, rootGetters) {
      const { keyword, timeframe_start_date, timeframe_end_date, submitted_start_date, submitted_end_date, freelancers, projects, agencies, status } = filter
      const isFreelancer = rootGetters['auth/isFreelancer']
      let result
      if (isFreelancer) {
        result = keyword.length || (timeframe_start_date && timeframe_end_date) || (submitted_start_date && submitted_end_date) || projects.length || status.length
      } else {
        result = keyword.length || (timeframe_start_date && timeframe_end_date) || (submitted_start_date && submitted_end_date) || freelancers.length || agencies.length || projects.length || status.length
      }
      return !!result
    },
    filterTags ({ filter }, getters, rootState, rootGetters) {
      const { keyword, timeframe_start_date, timeframe_end_date, submitted_start_date, submitted_end_date, freelancers, agencies, projects, status } = filter
      const tags = []
      if (freelancers.length) {
        freelancers.forEach(value => {
          const isFreelancer = rootGetters['auth/isFreelancer']
          if (!isFreelancer) {
            const tag = { filter: 'freelancers', value: `${value.first_name} ${value.last_name}`, id: value.id }
            tags.push(tag)
          }
        })
      }
      if (projects.length) {
        projects.forEach(value => {
          const tag = { filter: 'projects', value: value.name, id: value.id }
          tags.push(tag)
        })
      }
      if (agencies.length) {
        agencies.forEach(value => {
          const tag = { filter: 'agencies', value: value.name, id: value.id }
          tags.push(tag)
        })
      }
      if (status.length) {
        status.forEach(value => {
          const tag = { filter: 'status', value: timeRecordingStatusKeys[value] }
          tags.push(tag)
        })
      }
      keyword.forEach(value => {
        const tag = { filter: 'keyword', value }
        tags.push(tag)
      })

      if (timeframe_start_date && timeframe_end_date) {
        const startSplit = timeframe_start_date.split('-')
        const endSplit = timeframe_end_date.split('-')
        const start = `${startSplit[2]}.${startSplit[1]}.${startSplit[0].substring(2, 4)}`
        const end = `${endSplit[2]}.${endSplit[1]}.${endSplit[0].substring(2, 4)}`
        tags.push({
          filter: 'duration',
          value: `${start} - ${end}`
        })
      }

      if (submitted_start_date && submitted_end_date) {
        const startSplit = submitted_start_date.split('-')
        const endSplit = submitted_end_date.split('-')
        const start = `${startSplit[2]}.${startSplit[1]}.${startSplit[0].substring(2, 4)}`
        const end = `${endSplit[2]}.${endSplit[1]}.${endSplit[0].substring(2, 4)}`
        tags.push({
          filter: 'submitted',
          value: `${start} - ${end}`
        })
      }
      return tags
    },
    finalFilter ({ filter, selectedTab }, getters, rootState, rootGetters) {
      const finalFilter = cloneDeep(filter)
      if (!getters.hasAnyFilterApplied) {
        const { pending, declined, approved, financeRejected, financeApproved } = timeRecordingStatuses
        switch (selectedTab) {
        case timeRecordingFilterTabs.pending:
          finalFilter.status = [pending, financeRejected]
          break
        case timeRecordingFilterTabs.declined:
          finalFilter.status = [declined]
          break
        case timeRecordingFilterTabs.approved:
          finalFilter.status = [approved]
          break
        case timeRecordingFilterTabs.financeApproved:
          finalFilter.status = [financeApproved]
          break
        }
      }
      return finalFilter
    }
  },
  mutations: {
    resetStore ($state) {
      Object.assign($state, state())
    },
    switchTab (state, tab) {
      state.selectedTab = tab
    },
    updateFilter (state, { timeframe_start_date, timeframe_end_date, submitted_start_date, submitted_end_date, freelancers, projects, agencies, status }) {
      state.filter.timeframe_start_date = timeframe_start_date
      state.filter.timeframe_end_date = timeframe_end_date
      state.filter.submitted_start_date = submitted_start_date
      state.filter.submitted_end_date = submitted_end_date
      state.filter.freelancers = freelancers
      state.filter.projects = projects
      state.filter.agencies = agencies
      state.filter.status = status
    },
    addKeyword (state, keyword) {
      state.filter.keyword.push(keyword)
    },
    removeFilter (state, { filter, value, id }) {
      if (state.filter[filter]) {
        if (filter === 'projects' || filter === 'freelancers' || filter === 'agencies') {
          const index = state.filter[filter].findIndex(item => id === item.id)
          index !== -1 && state.filter[filter].splice(index, 1)
        } else if (filter === 'status') {
          const index = state.filter[filter].findIndex(item => timeRecordingStatuses[value] === item)
          index !== -1 && state.filter[filter].splice(index, 1)
        } else {
          const index = state.filter[filter].findIndex(item => value === item)
          index !== -1 && state.filter[filter].splice(index, 1)
        }
      } else if (filter === 'duration') {
        state.filter.timeframe_end_date = null
        state.filter.timeframe_start_date = null
      } else if (filter === 'submitted') {
        state.filter.submitted_end_date = null
        state.filter.submitted_start_date = null
      }
    }
  },
  actions: {
    switchTab ({ commit, dispatch }, tab) {
      commit('switchTab', tab)
      dispatch('timeRecording/reloadTimeRecording', null, { root: true })
    },
    updateFilter ({ commit, dispatch }, data) {
      commit('updateFilter', data)
      addFilterEvent()
      dispatch('timeRecording/reloadTimeRecording', null, { root: true })
    },
    addKeyword ({ commit, dispatch }, keyword) {
      commit('addKeyword', keyword)
      addSearchEvent(keyword)
      dispatch('timeRecording/reloadTimeRecording', null, { root: true })
    },
    removeFilter ({ commit, dispatch }, data) {
      commit('removeFilter', data)
      dispatch('timeRecording/reloadTimeRecording', null, { root: true })
    }
  }
}
